import { useCallback, useMemo } from "react";
import { TIME_WINDOW_ALL_TIME } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.constants";
import {
  useQueryBasicTasks,
  useQueryMinimalTasksRuns,
} from "src/queries/tasks/tasks.query";
import type { EmptyTaskOption } from "src/queries/tasks/tasks.types";
import { ALL_TASKS } from "./TaskSelector.constants";
import type { TaskWithDisabled, UseTaskSelectorProps } from "./TaskSelector.types";

const ALL_TASKS_OPTION: EmptyTaskOption = { task_name: ALL_TASKS };

export const useTaskSelector = ({
  app_pit,
  env,
  task_name,
  app_name,
  disabled,
  alwaysShowAllTasksOption = true,
}: UseTaskSelectorProps = {}) => {
  const tasksQuery = useQueryBasicTasks({
    params: {
      env_name: env,
      app_name,
      // Token stays on the body (stable key). Adapter omits bounds on the wire;
      // TimeWindowFilter then uses its unbounded default — every task in the app.
      timeWindow: TIME_WINDOW_ALL_TIME,
    },
    enabled: !disabled,
    caller: "useTaskSelector",
  });

  const tasksRunsQuery = useQueryMinimalTasksRuns({
    params: { app_pit, app_name, env_name: env },
    caller: "useTaskSelector",
    enabled: !disabled,
  });

  const tasks: TaskWithDisabled[] = useMemo(() => {
    const taskRuns = tasksRunsQuery.data ?? [];
    // Build Set of task names that have runs for O(1) lookup performance
    const taskRunNames = new Set(taskRuns.map((tr) => tr.task_name));

    // Guard: only mark tasks as disabled after taskRuns query finishes loading
    // This prevents all tasks from being temporarily disabled during initial load
    const shouldCheckDisabled = tasksRunsQuery.isFetched && !!app_pit;

    // Add 'disabled' property to tasks that don't exist in task runs
    const tasksWithDisabled = (tasksQuery.data ?? []).map((task) => ({
      ...task,
      disabled: shouldCheckDisabled && !taskRunNames.has(task.task_name),
    }));

    // Prepend "All Tasks" option only when there are 2+ tasks and `alwaysShowAllTasksOption` is true (casted to 1 or 0)
    return (tasksQuery.data?.length ?? 0) > +!alwaysShowAllTasksOption
      ? ([ALL_TASKS_OPTION] as TaskWithDisabled[]).concat(tasksWithDisabled)
      : tasksWithDisabled;
  }, [app_pit, tasksQuery.data, tasksRunsQuery.data, tasksRunsQuery.isFetched]);

  const task: TaskWithDisabled | undefined = useMemo(
    () =>
      task_name
        ? tasks?.find((task) => task.task_name === task_name)
        : ALL_TASKS_OPTION,
    [tasks, task_name]
  );

  const getTaskRunByName = useCallback(
    (task_name: string | undefined) =>
      task_name
        ? tasksRunsQuery.data?.find((task) => task.task_name === task_name)
        : undefined,
    [tasksRunsQuery.data]
  );

  return {
    tasksRunsQuery,
    isLoading: tasksQuery.isFetching,
    tasks,
    task,
    getTaskRunByName,
  };
};
